import { useState, useEffect } from 'react'
import Paginate from '../pagination/paginate'
import { useNavigate } from 'react-router-dom'
import { appGetHubById } from '../../store/hubs'
import { appGetAllUser, appUpdateUserByAdmin, appUploadUser } from '../../store/user'
import { useDispatch, useSelector } from 'react-redux'
import * as XLSX from 'xlsx'
import swal from 'sweetalert'
import PostPreview from '../PostPreview/PostPreview'
import ExportButton from '../../common/ExportButton'
import { sansthaUserExportColumns } from '../../config/exportColumns/sansthaUserExportColumns'

function Users({ hubDetails, sansthaDetails, users, userpage, setUserPage }) {
	const dispatch = useDispatch()
	const navigate = useNavigate()

	const [isUploading, setIsUploading] = useState(false)
	const [openMenuId, setOpenMenuId] = useState(null)
	const [searchType, setSearchType] = useState('firstName')
	const [search, setSearch] = useState('')
	const [activeFilters, setActiveFilters] = useState({})

	const userspaginate = useSelector(state => state.usersReducer.paginate)
	const userLabel = sansthaDetails && sansthaDetails._id ? 'Member' : 'User'

	useEffect(() => {
		const params = {
			page: userpage,
			limit: 10,
			...activeFilters,
		}

		if (sansthaDetails?._id) {
			params.sansthaId = sansthaDetails._id
		} else if (hubDetails?._id) {
			params.hub = hubDetails._id
		}

		dispatch(appGetAllUser(params))
	}, [userpage, activeFilters, sansthaDetails?._id, hubDetails?._id])

	// useEffect(() => {
	// 	if (hubDetails?._id) {
	// 		setActiveFilters({ hub: hubDetails._id })
	// 	}
	// }, [hubDetails])

	useEffect(() => {
		const closeMenu = () => setOpenMenuId(null)
		document.addEventListener('click', closeMenu)
		return () => document.removeEventListener('click', closeMenu)
	}, [])

	const handleDownloadExcel = () => {
		const sampleData = [
			{
				firstName: 'John',
				lastName: 'Doe',
				mobile: '9876543210',
			},
			{
				firstName: 'Jane',
				lastName: 'Smith',
				mobile: '9876500000',
			},
		]

		const wb = XLSX.utils.book_new()

		const wsData = XLSX.utils.json_to_sheet(sampleData, { origin: 'A1' })

		wsData['!cols'] = [{ wch: 20 }, { wch: 20 }, { wch: 15 }]

		XLSX.utils.book_append_sheet(wb, wsData, sansthaDetails ? 'Members' : 'Users')

		const filename = sansthaDetails ? 'sanstha_members_template.xlsx' : 'hub_users_template.xlsx'

		XLSX.writeFile(wb, filename)
	}

	const handleExcelImport = event => {
		const file = event.target.files[0]
		if (!file) return

		setIsUploading(true)

		if (hubDetails && hubDetails._id) {
			dispatch(
				appUploadUser({
					hub: hubDetails._id,
					file,
				}),
			)
				.then(() => {
					swal('Success', 'Users uploaded successfully!', 'success')
					dispatch(appGetAllUser({ page: 1, limit: 10, hub: hubDetails._id }))
					dispatch(appGetHubById(hubDetails._id))
				})
				.catch(error => {
					swal('Upload Failed', error?.response?.data?.message || 'Failed to upload users', 'error')
				})
				.finally(() => {
					setIsUploading(false)
				})
		} else if (sansthaDetails && sansthaDetails._id) {
			dispatch(
				appUploadUser({
					hub: sansthaDetails.hub._id,
					sansthaId: sansthaDetails._id,
					file,
				}),
			)
				.then(() => {
					swal('Success', 'Members uploaded successfully!', 'success')
					dispatch(
						appGetAllUser({
							page: 1,
							limit: 10,
							sansthaId: sansthaDetails._id,
						}),
					)
				})
				.catch(error => {
					swal(
						'Upload Failed',
						error?.response?.data?.message || 'Failed to upload members',
						'error',
					)
				})
				.finally(() => {
					setIsUploading(false)
				})
		}

		event.target.value = ''
	}

	const handleBlockUser = (userId, isCurrentlyBlocked) => {
		swal({
			title: isCurrentlyBlocked ? 'Unblock User?' : 'Block User?',
			text: isCurrentlyBlocked
				? 'Are you sure you want to unblock this user?'
				: 'Please provide a reason for blocking this user',
			icon: 'warning',
			content: !isCurrentlyBlocked
				? {
						element: 'input',
						attributes: {
							placeholder: 'Enter reason (required)',
							type: 'text',
						},
				  }
				: null,
			buttons: {
				cancel: true,
				confirm: {
					text: 'Confirm',
					closeModal: false,
				},
			},
			dangerMode: !isCurrentlyBlocked,
		}).then(reason => {
			if (reason === null) return

			if (!isCurrentlyBlocked && (!reason || !reason.trim())) {
				swal({
					title: 'Error',
					text: 'Reason is required to block a user',
					icon: 'error',
				})
				return
			}

			dispatch(
				appUpdateUserByAdmin({
					id: userId,
					isUserBlocked: !isCurrentlyBlocked,
					// blockReason: reason?.trim() || null,
					navigate,
				}),
			).then(() => {
				swal(
					'Success',
					`User ${isCurrentlyBlocked ? 'unblocked' : 'blocked'} successfully`,
					'success',
				)
				dispatch(appGetAllUser({ page: 1, limit: 10 }))
			})
		})
	}

	const getStatusBadgeClass = status => {
		switch (status) {
			case 'Active':
				return 'badge-success'
			case 'Inactive':
				return 'badge-danger'
			case 'Expired':
				return 'badge-warning'
			default:
				return 'badge-secondary'
		}
	}

	const handleSearch = () => {
		const filters = {}

		if (search) {
			filters[searchType] = search.trim()
		}

		setActiveFilters(filters)
		setUserPage(1)
	}
	const handleReset = () => {
		setSearch('')
		setSearchType('firstName')
		setActiveFilters({})
		setUserPage(1)
	}

	return (
		<div>
			<div className="mb-3 mr-2 d-flex justify-content-between align-items-center">
				<h4 className="ml-2 text-black">
					Total {userLabel}s: {hubDetails?.users?.length || sansthaDetails?.users?.length || 0}
				</h4>

				<div className="d-flex align-items-center mb-3 gap-2">
					<select
						className="form-control"
						value={searchType}
						onChange={e => setSearchType(e.target.value)}
						style={{ width: '180px' }}
					>
						<option value="firstName">First Name</option>
						<option value="lastName">Last Name</option>
						<option value="mobile">Mobile</option>
						<option value="email">Email</option>
					</select>

					<input
						type="text"
						className="form-control ml-1"
						placeholder={`Search by ${searchType}`}
						value={search}
						onChange={e => setSearch(e.target.value)}
						style={{ width: '220px' }}
					/>

					<button className="btn btn-info mx-2" onClick={handleSearch}>
						Search
					</button>

					<button className="btn btn-secondary" onClick={handleReset}>
						Reset
					</button>
				</div>

				<div className="dt-buttons btn-group d-flex gap-2">
					<ExportButton
						fetchAction={params =>
							dispatch(
								appGetAllUser({
									...params,
									...(hubDetails?._id && { hub: hubDetails._id }),
									...(sansthaDetails?._id && { sansthaId: sansthaDetails._id }),
									loading: false,
								}),
							)
						}
						filters={{}}
						columns={sansthaUserExportColumns}
						fileName={sansthaDetails ? 'Sanstha_Members' : 'Hub_Users'}
						sheetName={sansthaDetails ? 'Members' : 'Users'}
						buttonText={`Export ${userLabel}s`}
					/>

					<button
						className="btn btn-default buttons-excel buttons-html5 btn-info"
						onClick={handleDownloadExcel}
						style={{ minWidth: '170px', height: '38px', marginLeft: '10px', marginRight: '10px' }}
					>
						Download {userLabel} Template
					</button>

					<label
						className={`btn btn-info d-flex align-items-center gap-2 ${
							isUploading ? 'disabled' : ''
						}`}
						style={{
							minWidth: '170px',
							height: '38px',
							cursor: isUploading ? 'not-allowed' : 'pointer',
						}}
					>
						{isUploading && (
							<span
								className="spinner-border spinner-border-sm"
								role="status"
								aria-hidden="true"
							></span>
						)}
						{isUploading ? `Uploading ${userLabel}...` : `Upload ${userLabel}`}

						<input
							type="file"
							accept=".xlsx, .xls"
							style={{ display: 'none' }}
							onChange={handleExcelImport}
							disabled={isUploading}
						/>
					</label>
				</div>
			</div>

			<div className="table-responsive">
				<table className="table table-bordered table-hover">
					<thead className="">
						<tr>
							<th>Sr. No.</th>
							<th>{userLabel}</th>
							<th>Mobile</th>
							<th>Native Address</th>
							<th>Present Address</th>
							<th>Joined Date</th>
							{sansthaDetails?._id && (
								<>
									<th>Membership Start Date</th>
									<th>Membership End Date</th>
									<th>Membership Type</th>
								</>
							)}
							<th>Premium Member</th>
							{sansthaDetails?._id && <th>Membership Status</th>}

							<th>Actions</th>
						</tr>
					</thead>
					<tbody>
						{users?.length > 0 ? (
							users.map((user, index) => {
								const matchedMembership = user?.joinedSanstha?.find(
									item => item?.sanstha?._id === sansthaDetails?._id,
								)
								return (
									<tr
										key={index}
										onClick={e => {
											if (
												e.target.closest('td:last-child') ||
												e.target.closest('button') ||
												e.target.closest('.btn')
											) {
												return
											}
											navigate(`/user/details/${user?._id}`)
										}}
										style={{
											cursor: 'pointer',
											backgroundColor: user?.isUserBlocked ? '#ffe6e6' : 'inherit',
										}}
									>
										<td>{index + 1 + (userpage - 1) * 10}</td>
										<td>
											<PostPreview value={user?.profilePic || '/img/user.jpg'} />
											{user.firstName} {user.lastName}
										</td>
										<td>{user.mobile}</td>
										<td>
											{user?.nativePlaceAddress && user?.nativeArea && (
												<>
													{user?.nativePlaceAddress}, {user?.nativeArea}
												</>
											)}

											<br />
											{user?.nativeBlock &&
												user?.nativeDistrict &&
												user?.nativeState &&
												user?.nativePlacePincode && (
													<>
														<span>
															{' '}
															{user?.nativeBlock}, {user?.nativeDistrict} ,{user?.nativeState}{' '}
															{user?.nativePlacePincode}
														</span>
													</>
												)}
											<br />
										</td>
										<td>
											{user?.presentAddress && user?.presentnativeArea && (
												<>
													{user?.presentAddress}, {user?.presentnativeArea}
												</>
											)}
											<br />
											{user?.presentBlock &&
												user?.presentDistrict &&
												user?.presentState &&
												user?.presentPincode && (
													<>
														<span>
															{user?.presentBlock}, {user?.presentDistrict} ,{user?.presentState}{' '}
															{user?.presentPincode}
														</span>
													</>
												)}
										</td>
										<td>
											{new Date(user?.joinedHubDate).toLocaleDateString('en-US', {
												year: 'numeric',
												month: 'short',
												day: 'numeric',
											})}
										</td>

										{sansthaDetails?._id && (
											<>
												<td>
													{matchedMembership?.membershipStartDate
														? new Date(matchedMembership.membershipStartDate).toLocaleDateString(
																'en-US',
																{
																	year: 'numeric',
																	month: 'short',
																	day: 'numeric',
																},
														  )
														: '-'}
												</td>

												<td>
													{matchedMembership?.membershipEndDate
														? new Date(matchedMembership.membershipEndDate).toLocaleDateString(
																'en-US',
																{
																	year: 'numeric',
																	month: 'short',
																	day: 'numeric',
																},
														  )
														: '-'}
												</td>

												<td>{matchedMembership?.membershipType || '-'}</td>
											</>
										)}

										<td>
											{user?.premium === true ? (
												<span className="badge badge-primary">Premium Membership</span>
											) : (
												'-'
											)}
										</td>

										{sansthaDetails?._id && (
											<td>
												{matchedMembership?.status ? (
													<span
														className={`badge ${getStatusBadgeClass(matchedMembership.status)}`}
													>
														{matchedMembership.status} Membership
													</span>
												) : (
													'-'
												)}
											</td>
										)}

										<td onClick={e => e.stopPropagation()} style={{ position: 'relative' }}>
											<button
												className="btn btn-link"
												onClick={e => {
													e.stopPropagation()
													setOpenMenuId(openMenuId === user._id ? null : user._id)
												}}
											>
												<em className="fa fa-ellipsis-v fa-lg"></em>
											</button>

											{openMenuId === user?._id && (
												<div
													className="dropdown-menu show"
													style={{
														position: 'absolute',
														right: 0,
														top: 80,
														zIndex: 1000,
														display: 'block',
														left: -120,
													}}
												>
													<button
														className="dropdown-item"
														onClick={() => {
															setOpenMenuId(null)
															navigate(`/user/details/${user._id}`)
														}}
													>
														<em className="fa fa-eye mr-2"></em>
														Details
													</button>

													<button
														className="dropdown-item text-danger"
														onClick={() => {
															setOpenMenuId(null)
															handleBlockUser(user._id, user.isUserBlocked)
														}}
													>
														<em
															className={`fa ${user.isUserBlocked ? 'fa-unlock' : 'fa-lock'} mr-2`}
														></em>
														{user.isUserBlocked ? 'Unblock User' : 'Block User'}
													</button>
												</div>
											)}
										</td>
									</tr>
								)
							})
						) : (
							<tr>
								<td colSpan="6" className="text-center">
									No {userLabel}s data available
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>
			{userspaginate && <Paginate paginate={userspaginate} page={userpage} setPage={setUserPage} />}
		</div>
	)
}
export default Users
